import React from 'react';
import { RefreshCw } from 'lucide-react';
import { Category } from '../types';
import { getApiUrl } from '../../config/api';

interface CategorySelectorsProps {
  categories: Category[];
  loadingCategories: boolean;
  selectedCategory: string;
  selectedSubcategory: string;
  onCategoryChange: (categoryId: string) => void;
  onSubcategoryChange: (subcategoryId: string) => void;
  onRefreshCategories: () => void;
  setCategories: (categories: Category[]) => void;
  setLoadingCategories: (loading: boolean) => void;
}

export const CategorySelectors: React.FC<CategorySelectorsProps> = ({
  categories,
  loadingCategories,
  selectedCategory,
  selectedSubcategory,
  onCategoryChange,
  onSubcategoryChange,
  onRefreshCategories,
  setCategories,
  setLoadingCategories,
}) => {
  const handleRefresh = () => {
    if (onRefreshCategories) {
      onRefreshCategories();
    } else if (setLoadingCategories && setCategories) {
      setLoadingCategories(true);
      fetch(getApiUrl('/api/templates/v1.0.0/stats'))
        .then((res) => res.json())
        .then((result) => {
          if (result.status === 'success' && Array.isArray(result.data)) {
            setCategories(result.data);
          }
        })
        .catch((err) => console.error('Error refreshing categories:', err))
        .finally(() => setLoadingCategories(false));
    }
  };

  const selectedCategoryData = categories.find(
    (c) => c.categoryId === selectedCategory
  );
  const availableSubcategories = selectedCategoryData?.subcategories || [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Category Selector */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label
            htmlFor="categories"
            className="block text-sm font-semibold text-gray-700"
          >
            Category *
          </label>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loadingCategories}
            className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-all flex items-center gap-1"
            title="Refresh categories list"
          >
            <RefreshCw
              className={`w-3 h-3 ${loadingCategories ? 'animate-spin' : ''}`}
            />
            <span className="text-xs">Refresh</span>
          </button>
        </div>
        <select
          id="categories"
          value={selectedCategory}
          onChange={(e) => onCategoryChange(e.target.value)}
          disabled={loadingCategories}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50"
        >
          <option value="">Select a category</option>
          {categories.map((category) => (
            <option key={category.categoryId} value={category.categoryId}>
              {category.categoryName}
            </option>
          ))}
        </select>
      </div>

      {/* Subcategory Selector */}
      <div>
        <label
          htmlFor="subCategories"
          className="block text-sm font-semibold text-gray-700 mb-2"
        >
          Subcategory *
        </label>
        <select
          id="subCategories"
          value={selectedSubcategory}
          onChange={(e) => onSubcategoryChange(e.target.value)}
          disabled={!selectedCategory || loadingCategories}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50"
        >
          <option value="">Select a subcategory</option>
          {availableSubcategories.map((subcategory) => (
            <option
              key={subcategory.subCategoryId}
              value={subcategory.subCategoryId}
            >
              {subcategory.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
